import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createAdminDropdownOption,
  deleteAdminDropdownOption,
  getDropdownOptionUsage,
  listAdminDropdownCategories,
  listAdminDropdownOptions,
  reorderAdminDropdownOptions,
  updateAdminDropdownOption,
} from '@/api/adminDropdowns'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import type {
  DropdownAdminOption,
  DropdownOptionCreatePayload,
  DropdownOptionUpdatePayload,
  DropdownOptionUsage,
} from '@/types/adminDropdowns'

interface CrudMutationArgs {
  category: string
}

interface UpdateMutationArgs extends CrudMutationArgs {
  id: string
  payload: DropdownOptionUpdatePayload
}

interface CreateMutationArgs extends CrudMutationArgs {
  payload: DropdownOptionCreatePayload
}

interface DeleteMutationArgs extends CrudMutationArgs {
  id: string
}

interface ReorderMutationArgs extends CrudMutationArgs {
  orderedIds: string[]
}

function invalidateDropdownQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  category: string
) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.adminDropdowns.byCategory(category),
  })
  queryClient.invalidateQueries({ queryKey: queryKeys.dropdowns.all })
}

export function useAdminDropdownCategories() {
  return useQuery<string[]>({
    queryKey: queryKeys.adminDropdowns.categories(),
    queryFn: listAdminDropdownCategories,
  })
}

export function useAdminDropdownOptions(category: string | null) {
  return useQuery<DropdownAdminOption[]>({
    queryKey: queryKeys.adminDropdowns.byCategory(category ?? ''),
    queryFn: () => listAdminDropdownOptions(category!),
    enabled: !!category,
  })
}

export function useCreateAdminDropdownOption() {
  const queryClient = useQueryClient()

  return useMutation<DropdownAdminOption, Error, CreateMutationArgs>({
    mutationFn: ({ category, payload }) =>
      createAdminDropdownOption(category, payload),
    onSuccess: (_, variables) => {
      invalidateDropdownQueries(queryClient, variables.category)
      toast.success('Dropdown option created')
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Failed to create dropdown option'))
    },
  })
}

export function useUpdateAdminDropdownOption() {
  const queryClient = useQueryClient()

  return useMutation<DropdownAdminOption, Error, UpdateMutationArgs>({
    mutationFn: ({ category, id, payload }) =>
      updateAdminDropdownOption(category, id, payload),
    onSuccess: (_, variables) => {
      invalidateDropdownQueries(queryClient, variables.category)
      toast.success('Dropdown option updated')
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Failed to update dropdown option'))
    },
  })
}

export function useDeleteAdminDropdownOption() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteMutationArgs>({
    mutationFn: ({ category, id }) => deleteAdminDropdownOption(category, id),
    onSuccess: (_, variables) => {
      invalidateDropdownQueries(queryClient, variables.category)
      toast.success('Dropdown option deactivated')
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Failed to delete dropdown option'))
    },
  })
}

export function useReorderAdminDropdownOptions() {
  const queryClient = useQueryClient()

  return useMutation<DropdownAdminOption[], Error, ReorderMutationArgs>({
    mutationFn: ({ category, orderedIds }) =>
      reorderAdminDropdownOptions(category, orderedIds),
    onSuccess: (_, variables) => {
      invalidateDropdownQueries(queryClient, variables.category)
      toast.success('Dropdown options reordered')
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Failed to reorder dropdown options'))
    },
  })
}

export function useDropdownOptionUsage(
  category: string | null,
  value: string | null
) {
  return useQuery<DropdownOptionUsage>({
    queryKey: queryKeys.adminDropdowns.usage(category ?? '', value ?? ''),
    queryFn: () => getDropdownOptionUsage(category!, value!),
    enabled: !!category && !!value,
  })
}
